import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
  Toast,
  ToastBody,
  ToastHeader,
} from "reactstrap";
import { getAllRoleList } from "../../slices/rolesAndPermission/thunk";
import {
  deleteUser,
  getAllUsersList,
  getUserById,
  updateUser,
} from "../../slices/userManagement/thunk";
import {
  hideErrorMessage,
  setLoading,
} from "../../slices/userManagement/reducer";
import * as Yup from "yup";
import { Formik } from "formik";
import { TripModal } from "../../Components/Common/TripModal/tripModal";
import DeleteModal from "../../Components/Common/DeleteModal";
import PhoneNumberField from "../../Components/Common/PhoneNumberField";
import { removeCountryCode } from "../../constant/utils";

export const UserManagementList = () => {
  const formikRef = useRef(null);
  const dispatch = useDispatch();

  const getRoles = useSelector((state) => state.Roles.rolesList);

  const usersList = useSelector((state) => state.UsersManagement.usersList);
  const userById = useSelector((state) => state.UsersManagement.userById);

  const loading = useSelector((state) => state.UsersManagement.loading);

  const [viewDetails, setViewDetails] = useState(false);
  const [addUserModal, setAddUserModal] = useState(false);

  const [deleteUserModal, setDeleteUserModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [toasterType, setToasterType] = useState("");

  const toggleViewDetailModal = () => {
    setViewDetails(!viewDetails);
  };

  const toggleAddUserModal = async () => {
    setAddUserModal(!addUserModal);
  };

  const toggleDeleteUserModal = async () => {
    setDeleteUserModal(!deleteUserModal);
  };

  const usersManagementSchema = Yup.object().shape({
    first_name: Yup.string()
      .trim()
      .min(1, "First Name is required")
      .required("First Name is required"),
    last_name: Yup.string()
      .trim()
      .min(1, "Last Name is required")
      .required("Last Name is required"),
    email: Yup.string().required("Email is required"),
    role: Yup.string().required("Role is required"),
    phone_number: Yup.string()
      .matches(/^\d{10,15}$/, "Phone Number must be valid")
      .required("Phone Number is required"),
  });

  const updateInitialValues = {
    first_name: userById?.first_name,
    email: userById?.email,
    last_name: userById?.last_name,
    role: userById?.role,
    license_number: userById?.license_number,
    completed_trips: userById?.completed_trips,
    working_hours: userById?.working_hours,
    phone_number: userById?.phone_number,
    country_code: userById?.country_code,
  };

  const showToaster = (isSuccess, message) => {
    setShowToast(true);
    setToasterType(isSuccess ? "success" : "danger");
    setToastMessage(message);
    setTimeout(() => {
      setShowToast(false);
      if (!isSuccess) {
        dispatch(hideErrorMessage());
      }
    }, 3000);
  };

  const handleUsersSubmit = async (values) => {
    try {
      const { success: isSuccess, message } = await dispatch(
        updateUser(userById.id, values)
      );
      if (isSuccess) {
        showToaster(isSuccess, message || "Operation successful");
      }
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while processing your request"
      );
    } finally {
      dispatch(getAllUsersList(currentPage, pageSize, ""));
      toggleAddUserModal();
    }
  };
  const handleDeleteUser = async () => {
    try {
      const { success: isSuccess, message } = await dispatch(
        deleteUser(userById.id)
      );
      if (isSuccess) {
        showToaster(isSuccess, message || "User Successfully Deleted");
      }
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while deleting the user"
      );
    } finally {
      dispatch(getAllUsersList(currentPage, pageSize, ""));
      toggleDeleteUserModal();
    }
  };

  useEffect(() => {
    try {
      dispatch(getAllRoleList("None", "None", ""));
      dispatch(getAllUsersList(currentPage, pageSize, ""));
    } catch (error) { }
  }, [dispatch, currentPage, pageSize]);

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = usersList?.results?.slice(startIndex, endIndex);

  return (
    <>
      <Card className="revenueCard mt-2 mt-sm-4">
        <CardBody>
          <div
            className="position-fixed top-0 end-0 p-3"
            style={{ zIndex: 1055 }}
          >
            {showToast && (
              <Toast isOpen={showToast}>
                <ToastHeader
                  icon={"danger" === toasterType ? "danger" : "success"}
                  toggle={() => setShowToast(false)}
                >
                  {"danger" === toasterType ? "Error" : "Success"}
                </ToastHeader>
                <ToastBody>{toastMessage}</ToastBody>
              </Toast>
            )}
          </div>
          <div className="table-responsive">
            <Table className="mb-0 ">
              <thead className="revenueTableHeader">
                <tr>
                  <th className="tableHeaderText">User ID</th>
                  <th className="tableHeaderText">Name</th>
                  <th className="tableHeaderText">Role</th>
                  <th className="tableHeaderText">Status</th>
                  <th className="tableHeaderText">Actions</th>
                </tr>
              </thead>
              {loading.read ? (
                <tbody>
                  <tr>
                    <td colSpan="8" className="text-center align-middle">
                      <Spinner color="primary" />
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody className="revenueTableBody">
                  {paginatedData?.length ? (
                    paginatedData?.map((trip, index) => (
                      <tr key={index}>
                        <td className="tableBodyText">
                          {startIndex + index + 1}
                        </td>
                        <td className="tableBodyText">
                          {trip.first_name} {trip.last_name}
                        </td>
                        <td className="tableBodyText">{trip.role_name}</td>
                        <td>
                          {" "}
                          <span
                            style={{
                              display: "inline-block",
                              width: "80px",
                              textAlign: "center",
                              backgroundColor: trip.is_active
                                ? "#28a74533"
                                : "#dc354533",
                              padding: "4px 8px",
                              borderRadius: "10px",
                              color: trip.is_active ? "#28a745" : "#dc3545",
                              fontWeight: 500,
                            }}
                          >
                            {trip.is_active ? "Active" : "Inactive"}{" "}
                          </span>{" "}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <i
                              className="ri-eye-fill icon cursor-pointer"
                              style={{ color: "#E8A014", fontSize: "15px" }}
                              onClick={() => {
                                dispatch(getUserById(trip.id));
                                dispatch(
                                  setLoading({ type: "read", value: false })
                                );
                                toggleViewDetailModal();
                              }}
                            />
                            <i
                              className="ri-pencil-fill icon cursor-pointer"
                              style={{ color: "#007AFF", fontSize: "15px" }}
                              onClick={() => {
                                dispatch(getUserById(trip.id));
                                dispatch(
                                  setLoading({ type: "read", value: false })
                                );
                                toggleAddUserModal();
                              }}
                            />
                            <i
                              className="ri-delete-bin-fill icon cursor-pointer"
                              style={{ color: "#FB0000", fontSize: "15px" }}
                              onClick={() => {
                                dispatch(getUserById(trip.id));
                                dispatch(
                                  setLoading({ type: "read", value: false })
                                );
                                toggleDeleteUserModal();
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="text-start text-sm-center" colSpan={7}>
                        <h3>No User Found</h3>
                      </td>
                    </tr>
                  )}
                </tbody>
              )}
            </Table>
          </div>
        </CardBody>
      </Card>
      <TripModal
        isModalOpen={viewDetails}
        toggleModal={toggleViewDetailModal}
        buttonText="Assign"
        closeText={"Close without saving"}
        title={"User Management"}
        tripText={"User Details"}
        editIconClick={() => {
          toggleViewDetailModal();
          dispatch(getUserById(userById.id));
          toggleAddUserModal();
        }}
        deleteIconClick={() => {
          toggleViewDetailModal();
          dispatch(getUserById(userById.id));
          toggleDeleteUserModal();
        }}
        showButtons={false}
        showDeleteIcon={false}
        showEditIcon={false}
        showcloseIcon={true}
      >
        <div className="px-3 d-flex flex-column gap-4">
          <Row className="mb-5">
            <div className="custom-box-value">
              <div className="d-flex items-center justify-space-between gap-3">
                <span className="userDetailLabel">User Name</span>
                <span className="userDetailValue">
                  {" "}
                  {userById?.first_name} {userById?.last_name}
                </span>
              </div>
            </div>

            <div className="custom-box-value">
              <div className="d-flex items-center justify-space-between gap-3">
                <span className="userDetailLabel">Role</span>
                <span className="userDetailValue">{userById?.role_name}</span>
              </div>
            </div>

            <div className="custom-box-value">
              <div className="d-flex items-center justify-space-between gap-3">
                <span className="userDetailLabel">Email</span>
                <span className="userDetailValue">{userById?.email}</span>
              </div>
            </div>

            <div
              style={{
                width: "100%",
                marginTop: "16px",
                paddingLeft: "20px",
                paddingRight: "20px",
                marginBottom: "0px",
              }}
            >
              <div className="d-flex items-center justify-space-between gap-3">
                <span className="userDetailLabel">Phone Number</span>
                <span className="userDetailValue">
                  {removeCountryCode(
                    userById?.country_code,
                    userById?.phone_number
                  )}
                </span>
              </div>
            </div>
          </Row>
        </div>
      </TripModal>

      <TripModal
        isModalOpen={addUserModal}
        toggleModal={toggleAddUserModal}
        buttonText={"Update"}
        closeText={"Close without saving"}
        title={"User Management"}
        tripText={"Update User"}
        showcloseIcon={true}
        size="lg"
        loading={loading.update}
        onSubmit={() => {
          if (formikRef.current) {
            formikRef.current.handleSubmit();
          }
        }}
      >
        <div className="px-3 d-flex gap-4">
          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={updateInitialValues}
            validationSchema={usersManagementSchema}
            onSubmit={handleUsersSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="first_name" className="passwordLabel">
                        First Name
                      </Label>
                      <Input
                        type="text"
                        name="first_name"
                        className="profileInput"
                        id="first_name"
                        placeholder="Enter First Name"
                        value={values.first_name}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        invalid={
                          touched.first_name && errors.first_name ? true : false
                        }
                      />
                      {touched.first_name && errors.first_name ? (
                        <FormFeedback type="invalid">
                          {errors.first_name}
                        </FormFeedback>
                      ) : null}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="last_name" className="passwordLabel">
                        Last Name
                      </Label>
                      <Input
                        type="text"
                        name="last_name"
                        className="profileInput"
                        id="last_name"
                        placeholder="Enter Last Name"
                        value={values.last_name}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        invalid={
                          touched.last_name && errors.last_name ? true : false
                        }
                      />
                      {touched.last_name && errors.last_name ? (
                        <FormFeedback type="invalid">
                          {errors.last_name}
                        </FormFeedback>
                      ) : null}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="email" className="passwordLabel">
                        Email
                      </Label>
                      <Input
                        type="text"
                        name="email"
                        className="profileInput"
                        id="email"
                        placeholder="Enter Email"
                        value={values.email}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        invalid={touched.email && errors.email ? true : false}
                      />
                      {touched.email && errors.email ? (
                        <FormFeedback type="invalid">
                          {errors.email}
                        </FormFeedback>
                      ) : null}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="role" className="passwordLabel">
                        Select Role
                      </Label>
                      <Input
                        type="select"
                        name="role"
                        className="profileInput"
                        id="role"
                        value={values.role}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={touched.role && errors.role ? true : false}
                      >
                        <option value="">Select Role</option>
                        {Array.isArray(getRoles) && getRoles.length > 0 ? (
                          getRoles
                            .filter((role) => role.name !== "Driver")
                            .map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.name}
                              </option>
                            ))
                        ) : (
                          <option disabled>No roles found</option>
                        )}
                      </Input>
                      {touched.role && errors.role ? (
                        <FormFeedback type="invalid">
                          {errors.role}
                        </FormFeedback>
                      ) : null}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <PhoneNumberField
                        label="Phone Number"
                        phoneValue={values.phone_number}
                        countryCode={updateInitialValues.country_code}
                        onChange={(phone, code) => {
                          formikRef.current.setFieldValue(
                            "phone_number",
                            phone
                          );
                          formikRef.current.setFieldValue("country_code", code);
                        }}
                        onBlur={handleBlur}
                      />
                      {touched.phone_number && errors.phone_number && (
                        <FormFeedback className="d-block">
                          {errors.phone_number}
                        </FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </div>
      </TripModal>

      <DeleteModal
        show={deleteUserModal}
        loading={loading.delete}
        onDeleteClick={() => {
          handleDeleteUser();
        }}
        onCloseClick={toggleDeleteUserModal}
      />
    </>
  );
};
